/**
 * O fato do DV360: uma linha do relatório da Bid Manager, no grão (Campaign, LI, dia) (§4.6).
 *
 * O custo é o Media Cost na moeda do Advertiser, EXATO, com até 6 casas: `Decimal`, nunca
 * `number`. As somas são exatas; o único arredondamento acontece na apresentação, nas casas da
 * moeda (`apresentarCustoDv360`). Impressões e cliques são inteiros sem teto prático: `bigint`.
 */
import { Decimal } from 'decimal.js'
import type { DataNominal } from '../data/data-nominal.js'
import { Money } from '../dinheiro/money.js'
import type { Moeda } from '../dinheiro/moeda.js'

/**
 * Construtor local, como o do `Money`: precisão de 40 dígitos significativos para que a soma de
 * 50.000 linhas de `numeric(20,6)` nunca arredonde por dentro (o padrão do decimal.js é 20), e sem
 * notação científica em `toString()`. Mexer na configuração global afetaria outras bibliotecas.
 */
export const DecimalDv360 = Decimal.clone({
  precision: 40,
  rounding: Decimal.ROUND_HALF_UP,
  toExpNeg: -40,
  toExpPos: 40,
})

export const ZERO_DV360: Decimal = new DecimalDv360(0)

export interface FatoDv360 {
  readonly advertiserId: string
  readonly moeda: string
  readonly campaignId: string
  readonly insertionOrderId: string
  readonly lineItemId: string
  /** Data nominal no fuso do Advertiser: o grão do CSV real [R]. */
  readonly dia: DataNominal
  readonly custo: Decimal
  readonly impressoes: bigint
  readonly cliques: bigint
}

/** O fato tem atividade: custo diferente de zero, ou alguma impressão ou clique (§4.6). */
export function fatoComAtividade(fato: Pick<FatoDv360, 'custo' | 'impressoes' | 'cliques'>): boolean {
  return !fato.custo.isZero() || fato.impressoes > 0n || fato.cliques > 0n
}

/**
 * O ÚNICO arredondamento do custo: ROUND_HALF_UP, nas casas da moeda, só na apresentação.
 * Quem soma valores já arredondados produz outro número — por isso a soma é sempre do exato.
 */
export function apresentarCustoDv360(custoExato: Decimal, moedaDoCusto: Moeda): Money {
  return Money.de(custoExato.toFixed(), moedaDoCusto).arredondar()
}
